// ─── OverviewPage.jsx — "Two Sides" redesign ─────────────────────────────────
import React, { useState, useEffect, useMemo } from "react";
import { supabase } from "../lib/supabaseClient";
import { useLanguage } from "./LanguageContext";
import { apiFetch } from "../lib/apiFetch";
import { useUser, PLAN_LABELS } from "./UserContext";
import { downloadAuditPdf } from "./AuditPdfDocument";
import AuditPdfDocument from "./AuditPdfDocument";
import { PDFViewer } from "@react-pdf/renderer";

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  bg:        "#faf9f6",
  bgSunken:  "#f3f1ec",
  bgElev:    "#ffffff",
  ink:       "#0d0d0d",
  ink2:      "#2a2a2a",
  ink3:      "#555550",
  ink4:      "#8a8780",
  line:      "#1a1a1a",
  lineSoft:  "rgba(13,13,13,0.08)",
  lineSofter:"rgba(13,13,13,0.04)",
  accent:    "#1f3df5",
  accentSoft:"rgba(31,61,245,0.08)",
  pos:       "#0c6b3c",
  neu:       "#8a7f00",
  neg:       "#b03030",
};

const FONTS = `
  @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Inter:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .ov-serif  { font-family: 'Instrument Serif', Georgia, serif; }
  .ov-mono   { font-family: 'JetBrains Mono', ui-monospace, monospace; }
  .ov-sans   { font-family: 'Inter', -apple-system, sans-serif; }
  .ov-eyebrow {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px; letter-spacing: 0.08em;
    text-transform: uppercase; color: ${C.ink3};
  }
  .ov-btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 8px 14px; font-size: 12px; font-weight: 500;
    border: 1px solid ${C.lineSoft}; background: ${C.bgElev};
    color: ${C.ink2}; cursor: pointer; font-family: 'Inter', sans-serif;
    transition: background 120ms;
  }
  .ov-btn:hover { background: ${C.bgSunken}; }
  .ov-btn-accent {
    background: ${C.accent}; color: #fff; border-color: ${C.accent};
  }
  .ov-btn-accent:hover { background: #0a1ba8; border-color: #0a1ba8; }
  @keyframes ov-spin { to { transform: rotate(360deg); } }
  @media (max-width: 768px) {
    .ov-two-sides { grid-template-columns: 1fr !important; }
    .ov-two-sides .ov-bridge { display: none !important; }
    .ov-portfolio-head { display: none !important; }
    .ov-portfolio-row { grid-template-columns: 1fr auto !important; }
    .ov-portfolio-row .ov-col-website,
    .ov-portfolio-row .ov-col-actions,
    .ov-portfolio-row .ov-col-bridge,
    .ov-portfolio-row .ov-col-trend { display: none !important; }
    .ov-header-row { flex-direction: column; gap: 12px !important; }
    .ov-header-btns { width: 100%; display: flex; gap: 8px; }
    .ov-header-btns button { flex: 1; }
    .ov-footer-grid { grid-template-columns: 1fr !important; }
  }
`;

// ─── Primitives ───────────────────────────────────────────────────────────────
function HBar({ value, color, height = 6 }) {
  const pct = Math.min(100, Math.max(0, value ?? 0));
  return (
    <div style={{ height, background: C.lineSoft, flex: 1 }}>
      <div style={{ height, width: `${pct}%`, background: color, transition: "width 400ms ease" }} />
    </div>
  );
}

function BrandGlyph({ name, size = 28 }) {
  const letter = (name?.[0] || "?").toUpperCase();
  const colors = ["#1f3df5","#0c6b3c","#b03030","#8a7f00","#555550","#2a2a2a"];
  const idx = (name?.charCodeAt(0) || 0) % colors.length;
  return (
    <div style={{
      width: size, height: size, flexShrink: 0,
      background: colors[idx] + "18", border: `1px solid ${colors[idx]}30`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.43, fontWeight: 600, color: colors[idx],
      fontFamily: "'Inter', sans-serif",
    }}>{letter}</div>
  );
}

function Eyebrow({ children, accent = false, style = {} }) {
  return (
    <div className="ov-eyebrow" style={{ color: accent ? C.accent : C.ink3, display: "flex", alignItems: "center", gap: 6, ...style }}>
      {children}
    </div>
  );
}

function SectionHeader({ eyebrow, title }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <Eyebrow style={{ marginBottom: 8 }}>{eyebrow}</Eyebrow>
      <div className="ov-serif" style={{ fontSize: 26, lineHeight: 1, letterSpacing: "-0.02em", color: C.ink }}>
        {title}
      </div>
    </div>
  );
}

// ─── Time helper ──────────────────────────────────────────────────────────────
function timeAgo(dateStr, lang) {
  if (!dateStr) return "—";
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  const l = {
    de: { s: "gerade", m: "Min", h: "Std", d: "T" },
    en: { s: "now",    m: "min", h: "hr",  d: "d" },
    fr: { s: "maintenant", m: "min", h: "h", d: "j" },
    it: { s: "ora",    m: "min", h: "ore", d: "g" },
  }[lang] || { s: "now", m: "min", h: "hr", d: "d" };
  if (diff < 60)   return l.s;
  if (diff < 3600) return `${Math.floor(diff/60)} ${l.m}`;
  if (diff < 86400) return `${Math.floor(diff/3600)} ${l.h}`;
  return `${Math.floor(diff/86400)} ${l.d}`;
}

// ─── PDF Export button (kept from existing) ───────────────────────────────────
function ExportPdfButton({ audit, t, lang }) {
  const [exporting, setExporting] = React.useState(false);
  const handle = async () => {
    setExporting(true);
    try { await downloadAuditPdf(audit, lang); }
    catch (err) { console.error("PDF error:", err); }
    finally { setExporting(false); }
  };
  return (
    <button onClick={handle} disabled={exporting} className="ov-btn ov-btn-accent" style={{ fontSize: 12 }}>
      {exporting
        ? <div style={{ width: 12, height: 12, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%", animation: "ov-spin 1s linear infinite" }} />
        : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
      }
      {t("geo.exportPdf")}
    </button>
  );
}

// ─── AggregateTwoSides hero band ──────────────────────────────────────────────
function AggregateTwoSides({ agg, t }) {
  const Stat = ({ value, label, color }) => (
    <div>
      <div className="ov-serif" style={{ fontSize: 26, lineHeight: 1, color: color || C.ink }}>{value ?? "—"}</div>
      <div className="ov-eyebrow" style={{ marginTop: 6, fontSize: 9 }}>{label}</div>
    </div>
  );

  const hasAudit = agg.avgAudit !== null;
  const hasViz   = agg.avgViz   !== null;

  return (
    <div style={{ border: `1px solid ${C.line}`, display: "grid", gridTemplateColumns: "1fr 120px 1fr", background: C.line, gap: 1 }} className="ov-two-sides">
      {/* Side A — Website */}
      <div style={{ background: C.bgSunken, padding: 32 }}>
        <Eyebrow>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          {t("overview.sideAEyebrow")}
        </Eyebrow>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 12, marginTop: 18 }}>
          <div className="ov-serif" style={{ fontSize: 88, lineHeight: 0.82, letterSpacing: "-0.04em", color: C.ink }}>
            {hasAudit ? agg.avgAudit : "—"}
            {hasAudit && <span className="ov-sans" style={{ fontSize: 30, color: C.ink4 }}>/100</span>}
          </div>
          <div style={{ marginBottom: 10 }}>
            <div className="ov-serif" style={{ fontSize: 20, lineHeight: 1.1, color: C.ink2 }}>{t("overview.sideATitle")}</div>
            <div style={{ fontSize: 12, color: C.ink3, marginTop: 4 }}>{t("overview.sideASub")}</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 36, marginTop: 24, paddingTop: 18, borderTop: `1px solid ${C.lineSoft}` }}>
          <Stat value={agg.openActions} label={t("overview.aggOpenActions")} color={C.neg} />
          <Stat value={agg.belowSixty} label={t("overview.aggBelow60")} color={C.neu} />
          <Stat value={agg.n} label={t("overview.aggBrands")} />
        </div>
      </div>

      {/* Bridge */}
      <div className="ov-bridge" style={{ background: C.ink, color: C.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "16px 12px", gap: 14 }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3">
          <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
        </svg>
        <div className="ov-mono" style={{ fontSize: 9, letterSpacing: "0.08em", lineHeight: 1.5, color: "rgba(255,255,255,0.6)", textTransform: "uppercase" }}>
          {t("overview.sideBridge")}
        </div>
      </div>

      {/* Side B — AI Visibility */}
      <div style={{ background: C.bgElev, padding: 32 }}>
        <Eyebrow accent>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
          {t("overview.sideBEyebrow")}
        </Eyebrow>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 12, marginTop: 18 }}>
          <div className="ov-serif" style={{ fontSize: 88, lineHeight: 0.82, letterSpacing: "-0.04em", color: C.accent }}>
            {hasViz ? agg.avgViz : "—"}
            {hasViz && <span className="ov-sans" style={{ fontSize: 36, color: C.ink3 }}>%</span>}
          </div>
          <div style={{ marginBottom: 10 }}>
            <div className="ov-serif" style={{ fontSize: 20, lineHeight: 1.1, color: C.ink2 }}>{t("overview.sideBTitle")}</div>
            <div style={{ fontSize: 12, color: C.ink3, marginTop: 4 }}>{t("overview.sideBSub")}</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 36, marginTop: 24, paddingTop: 18, borderTop: `1px solid ${C.lineSoft}` }}>
          <Stat value={agg.totalMentions} label={t("overview.aggMentions")} color={C.accent} />
          <Stat value={agg.n > 0 ? "#1" : "—"} label={t("overview.aggAvgViz")} />
          <Stat value="5" label="KI-Systeme" />
        </div>
      </div>
    </div>
  );
}

// ─── Portfolio Table ───────────────────────────────────────────────────────────
const OV_COLS = "minmax(0,1.5fr) 1.4fr 84px 28px 1.4fr 84px";

function PortfolioTable({ brands, t, lang, onNavigateToBrand }) {
  if (!brands.length) return (
    <div style={{ border: `1px solid ${C.lineSoft}`, background: C.bgElev, padding: "40px 24px", textAlign: "center" }}>
      <div style={{ fontSize: 13, color: C.ink4 }}>{t("overview.noBrandActivity")}</div>
    </div>
  );

  return (
    <div style={{ border: `1px solid ${C.line}`, background: C.bgElev }}>
      {/* Header row */}
      <div className="ov-portfolio-head" style={{ display: "grid", gridTemplateColumns: OV_COLS, gap: 16, padding: "12px 20px", borderBottom: `1px solid ${C.line}` }}>
        {[t("overview.brand"), t("overview.websiteScore"), t("overview.actions"), "", t("overview.visibility"), t("overview.lastRun")].map((h, i) => (
          <span key={i} className="ov-mono" style={{ fontSize: 10, letterSpacing: "0.08em", color: C.ink4, textTransform: "uppercase", textAlign: i >= 2 ? "right" : "left" }}>{h}</span>
        ))}
      </div>

      {brands.map((brand, i) => {
        const hasAudit = brand.auditScore !== null && brand.auditScore !== undefined;
        const hasViz   = brand.visibility  !== null && brand.visibility  !== undefined;
        const actions  = brand.openActions ?? null;

        return (
          <button key={brand.id} onClick={() => onNavigateToBrand?.(brand.id)}
            className="ov-portfolio-row"
            style={{
              display: "grid", gridTemplateColumns: OV_COLS, gap: 16,
              width: "100%", textAlign: "left", padding: "14px 20px",
              alignItems: "center", background: "transparent", cursor: "pointer",
              borderBottom: i < brands.length - 1 ? `1px solid ${C.lineSofter}` : "none",
              transition: "background 100ms",
            }}
            onMouseEnter={e => e.currentTarget.style.background = C.bgSunken}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>

            {/* Brand */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              <BrandGlyph name={brand.primary_name} size={28} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 500, color: C.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{brand.primary_name}</div>
                <div className="ov-mono" style={{ fontSize: 10, color: C.ink4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{brand.website_url}</div>
              </div>
            </div>

            {/* Website score */}
            <div className="ov-col-website" style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <HBar value={hasAudit ? brand.auditScore : 0} color={hasAudit && brand.auditScore < 60 ? C.neu : C.ink3} />
              <span className="ov-mono" style={{ fontSize: 12, minWidth: 24, textAlign: "right", color: C.ink2 }}>
                {hasAudit ? brand.auditScore : "—"}
              </span>
            </div>

            {/* Open actions */}
            <div className="ov-col-actions" style={{ textAlign: "right" }}>
              {actions !== null ? (
                <span className="ov-mono" style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  padding: "2px 8px", fontSize: 11,
                  background: actions > 0 ? C.bgSunken : "transparent",
                  color: actions >= 8 ? C.neg : actions >= 5 ? C.neu : C.ink4,
                  border: `1px solid ${C.lineSoft}`,
                }}>{actions} {t("overview.openLabel")}</span>
              ) : <span className="ov-mono" style={{ fontSize: 11, color: C.ink4 }}>—</span>}
            </div>

            {/* Bridge arrow */}
            <div className="ov-col-bridge" style={{ display: "flex", justifyContent: "center", color: C.ink4 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
              </svg>
            </div>

            {/* Visibility */}
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <HBar value={hasViz ? brand.visibility : 0} color={C.accent} />
              <span className="ov-mono" style={{ fontSize: 12, minWidth: 34, textAlign: "right", color: C.accent }}>
                {hasViz ? `${brand.visibility}%` : "—"}
              </span>
            </div>

            {/* Last run */}
            <span className="ov-mono ov-col-run" style={{ fontSize: 11, color: C.ink4, textAlign: "right" }}>
              {brand.lastRun ? timeAgo(brand.lastRun, lang) : "—"}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Activity Feed ────────────────────────────────────────────────────────────
function ActivityFeed({ brands, audits, t, lang }) {
  const events = useMemo(() => {
    const runs = brands
      .filter(b => b.lastRun)
      .map(b => ({
        type: "run",
        brand: b.primary_name,
        letter: b.primary_name?.[0]?.toUpperCase() || "?",
        metric: t("overview.actVisibility"),
        value: b.visibility !== null ? `${b.visibility}%` : "—",
        date: b.lastRun,
      }));

    const auditEvents = (audits || []).map(a => {
      const domain = a.url?.replace(/https?:\/\//, "").split("/")[0] || a.url;
      return {
        type: "audit",
        brand: domain,
        letter: domain?.[0]?.toUpperCase() || "?",
        metric: t("overview.actAuditScore"),
        value: a.overall_score !== null ? `${a.overall_score}/100` : "—",
        date: a.created_at,
      };
    });

    return [...runs, ...auditEvents]
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 10);
  }, [brands, audits, t]);

  if (!events.length) return (
    <div style={{ border: `1px solid ${C.lineSoft}`, background: C.bgElev, padding: "40px 24px", textAlign: "center" }}>
      <div style={{ fontSize: 13, color: C.ink4 }}>{t("overview.noActivity")}</div>
    </div>
  );

  return (
    <div style={{ border: `1px solid ${C.line}`, background: C.bgElev }}>
      {events.map((ev, i) => (
        <div key={i} style={{
          display: "grid", gridTemplateColumns: "90px 1fr auto auto",
          gap: 16, alignItems: "center", padding: "14px 20px",
          borderBottom: i < events.length - 1 ? `1px solid ${C.lineSofter}` : "none",
        }}>
          <span className="ov-mono" style={{
            fontSize: 9, letterSpacing: "0.06em", padding: "3px 7px", textAlign: "center",
            border: `1px solid ${C.lineSoft}`,
            color: ev.type === "run" ? C.accent : C.ink2,
            background: ev.type === "run" ? C.accentSoft : C.bgSunken,
          }}>
            {ev.type === "run" ? "AI-RUN" : "AUDIT"}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <BrandGlyph name={ev.brand} size={22} />
            <span style={{ fontSize: 13, fontWeight: 500, color: C.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ev.brand}</span>
            <span style={{ fontSize: 12, color: C.ink4 }}>· {ev.metric}</span>
          </div>
          <span className="ov-serif" style={{ fontSize: 18, color: ev.type === "run" ? C.accent : C.ink2 }}>{ev.value}</span>
          <span className="ov-mono" style={{ fontSize: 11, color: C.ink4, minWidth: 48, textAlign: "right" }}>
            {timeAgo(ev.date, lang)}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Feedback (kept from existing) ───────────────────────────────────────────
function StarRating({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div style={{ display: "flex", gap: 6 }}>
      {[1,2,3,4,5].map(i => (
        <svg key={i} width="22" height="22" viewBox="0 0 24 24"
          fill={i <= (hover || value) ? "#f59e0b" : "none"}
          stroke={i <= (hover || value) ? "#f59e0b" : C.ink4}
          strokeWidth="2" style={{ cursor: "pointer" }}
          onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(0)}
          onClick={() => onChange(i)}>
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
        </svg>
      ))}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function OverviewPage({ session, onNavigateToAudit, onNavigateToBrand, onShowWizard }) {
  const { t, lang } = useLanguage();
  const { plan, credits } = useUser() || {};
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSent, setFeedbackSent] = useState(false);
  const userId = session?.user?.id;

  useEffect(() => { if (userId) loadData(); }, [userId]);

  // Re-fetch when user returns to this tab (SPA navigation or window focus)
  useEffect(() => {
    if (!userId) return;
    const onVisible = () => { if (document.visibilityState === "visible") loadData(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [userId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`/overview-stats?userId=${userId}`);
      const json = await res.json();
      setData(json);
    } catch (err) { console.error("Overview load error:", err); }
    finally { setLoading(false); }
  };

  const handleFeedback = async () => {
    if (!feedbackRating && !feedbackText.trim()) return;
    try { await supabase.from("feedback").insert({ user_id: userId, rating: feedbackRating, text: feedbackText.trim(), created_at: new Date().toISOString() }); } catch {}
    setFeedbackSent(true);
  };

  const brands  = data?.brands  || [];
  const audits  = data?.audits  || [];
  const profile = data?.profile || {};

  // Aggregate metrics
  const agg = useMemo(() => {
    const n = brands.length;
    if (n === 0) return { n: 0, avgAudit: null, avgViz: null, openActions: null, belowSixty: null, totalMentions: null };

    const withAudit = brands.filter(b => b.auditScore !== null && b.auditScore !== undefined);
    const withViz   = brands.filter(b => b.visibility  !== null && b.visibility  !== undefined);

    const avgAudit  = withAudit.length ? Math.round(withAudit.reduce((s, b) => s + b.auditScore, 0) / withAudit.length) : null;
    const avgViz    = withViz.length   ? Math.round(withViz.reduce((s, b) => s + b.visibility, 0)   / withViz.length)   : null;
    const openActions = brands.reduce((s, b) => s + (b.openActions ?? 0), 0);
    const belowSixty  = withAudit.filter(b => b.auditScore < 60).length;
    const totalMentions = withViz.reduce((s, b) => s + Math.round((b.visibility / 100) * 10), 0);

    return { n, avgAudit, avgViz, openActions, belowSixty, totalMentions };
  }, [brands]);

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 300, background: C.bg }}>
      <style>{FONTS}</style>
      <div style={{ width: 28, height: 28, border: `2px solid ${C.lineSoft}`, borderTopColor: C.accent, borderRadius: "50%", animation: "ov-spin 1s linear infinite" }} />
    </div>
  );

  return (
    <div style={{ background: C.bg, minHeight: "100%", fontFamily: "'Inter', -apple-system, sans-serif" }}>
      <style>{FONTS}</style>

      {/* ── Header ── */}
      <div style={{ padding: "32px 32px 0" }}>
        <div className="ov-header-row" style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 24, flexWrap: "wrap" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <Eyebrow>§ PORTFOLIO</Eyebrow>
              {plan && (
                <span style={{ fontSize: 11, fontWeight: 700, color: "#2563eb", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 20, padding: "2px 10px", letterSpacing: "0.04em" }}>
                  {PLAN_LABELS[plan] ?? plan}
                </span>
              )}
              {credits !== null && credits !== undefined && (
                <span style={{ fontSize: 11, fontWeight: 500, color: "#64748b", background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 20, padding: "2px 10px" }}>
                  {credits} Credits
                </span>
              )}
            </div>
            <h1 className="ov-serif" style={{ fontSize: 44, lineHeight: 1, letterSpacing: "-0.025em", margin: 0, color: C.ink }}>
              {t("overview.title")}
            </h1>
            <p style={{ fontSize: 13, color: C.ink3, marginTop: 10, maxWidth: 540, lineHeight: 1.5, margin: "10px 0 0" }}>
              {t("overview.subtitle")}
            </p>
          </div>
          <div className="ov-header-btns" style={{ display: "flex", gap: 8 }}>
            <button className="ov-btn" onClick={() => onShowWizard?.()}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              {t("nav.addBrand")}
            </button>
            <button className="ov-btn ov-btn-accent" onClick={() => onNavigateToAudit?.()}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
              {t("overview.updateVisibility")}
            </button>
          </div>
        </div>

        {/* Run status strip */}
        {brands.length > 0 && (
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginTop: 22, paddingTop: 18, borderTop: `1px solid ${C.lineSoft}` }}>
            {brands[0]?.lastRun && (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.pos }} />
                <span className="ov-mono" style={{ fontSize: 10, letterSpacing: "0.08em", color: C.ink4, textTransform: "uppercase" }}>{t("overview.lastRun")}</span>
                <span className="ov-mono" style={{ fontSize: 11, color: C.ink2 }}>{timeAgo(brands[0].lastRun, lang)}</span>
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.ink4 }} />
              <span className="ov-mono" style={{ fontSize: 10, letterSpacing: "0.08em", color: C.ink4, textTransform: "uppercase" }}>{t("overview.engines")}</span>
              <span className="ov-mono" style={{ fontSize: 11, color: C.ink2 }}>ChatGPT · Claude · Perplexity · Gemini · Copilot</span>
            </div>
          </div>
        )}
      </div>

      {/* ── Content ── */}
      <div style={{ padding: "28px 32px 48px", display: "flex", flexDirection: "column", gap: 48 }}>

        {/* 1 · AggregateTwoSides */}
        <AggregateTwoSides agg={agg} t={t} />

        {/* 2 · Portfolio Table */}
        <section>
          <SectionHeader eyebrow={t("overview.brandsEyebrow")} title={t("overview.brandsTableTitle")} />
          <PortfolioTable brands={brands} t={t} lang={lang} onNavigateToBrand={onNavigateToBrand} />
        </section>

        {/* 3 · Activity Feed */}
        <section>
          <SectionHeader eyebrow={t("overview.activityEyebrow")} title={t("overview.activityTitle")} />
          <ActivityFeed brands={brands} audits={audits} t={t} lang={lang} />
        </section>

        {/* 4 · Footer: Founder note + Feedback */}
        <div className="ov-footer-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          {/* Founder note */}
          <div style={{ background: C.bgElev, border: `1px solid ${C.lineSoft}`, padding: 32 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <div style={{ width: 44, height: 44, background: C.ink, display: "flex", alignItems: "center", justifyContent: "center", color: C.bg, fontWeight: 700, fontSize: 16, flexShrink: 0, fontFamily: "'Instrument Serif', serif" }}>A</div>
              <div>
                <Eyebrow style={{ marginBottom: 2 }}>{t("overview.proofName")}</Eyebrow>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>{t("overview.founderNoteTitle")}</div>
              </div>
            </div>
            <div style={{ fontSize: 13, color: C.ink3, lineHeight: 1.7 }}>{t("overview.founderNote1")}</div>
            <div style={{ fontSize: 13, color: C.ink3, lineHeight: 1.7, marginTop: 8 }}>{t("overview.founderNote2")}</div>
            <div style={{ display: "flex", gap: 8, marginTop: 20, flexWrap: "wrap" }}>
              <a href="https://georev.ai/kontakt" target="_blank" rel="noopener noreferrer" className="ov-btn" style={{ textDecoration: "none" }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                {t("overview.helpCenter")}
              </a>
            </div>
          </div>

          {/* Feedback */}
          <div style={{ background: C.ink, padding: 32 }}>
            {feedbackSent ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 12, color: C.bg, textAlign: "center" }}>
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={C.pos} strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                <div style={{ fontSize: 15, fontWeight: 600 }}>{t("overview.feedbackThanks")}</div>
                <div style={{ fontSize: 13, color: C.ink4 }}>{t("overview.feedbackThanksDesc")}</div>
              </div>
            ) : (
              <>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.bg, marginBottom: 16 }}>{t("overview.feedbackTitle")}</div>
                <StarRating value={feedbackRating} onChange={setFeedbackRating} />
                <textarea value={feedbackText} onChange={e => setFeedbackText(e.target.value.slice(0, 800))}
                  placeholder={t("overview.feedbackPlaceholder")} rows={4}
                  style={{ width: "100%", marginTop: 14, padding: "10px 12px", border: `1px solid ${C.ink3}`, background: "rgba(255,255,255,0.06)", color: C.bg, fontSize: 13, resize: "none", outline: "none", boxSizing: "border-box", lineHeight: 1.5, fontFamily: "inherit" }} />
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8 }}>
                  <span className="ov-mono" style={{ fontSize: 10, color: C.ink4 }}>Max 800 {t("overview.chars")}</span>
                  <button onClick={handleFeedback} disabled={!feedbackRating && !feedbackText.trim()}
                    className="ov-btn"
                    style={{ background: feedbackRating || feedbackText.trim() ? C.bg : "transparent", color: feedbackRating || feedbackText.trim() ? C.ink : C.ink4, border: `1px solid ${feedbackRating || feedbackText.trim() ? C.bg : C.ink3}` }}>
                    {t("overview.feedbackSubmit")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Audit Detail Drawer (kept from existing) ── */}
      {selectedAudit && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "flex-end" }}
          onClick={e => { if (e.target === e.currentTarget) setSelectedAudit(null); }}>
          <div style={{ width: 760, maxWidth: "95vw", height: "100vh", background: "white", boxShadow: "-4px 0 24px rgba(0,0,0,0.15)", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "14px 20px", borderBottom: `1px solid ${C.lineSoft}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>{selectedAudit.url?.replace(/https?:\/\//, "").split("/")[0]}</div>
                <div className="ov-mono" style={{ fontSize: 10, color: C.ink4, marginTop: 2 }}>
                  {selectedAudit.created_at ? new Date(selectedAudit.created_at).toLocaleDateString() : ""}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <ExportPdfButton audit={selectedAudit} t={t} lang={lang} />
                <button onClick={() => setSelectedAudit(null)} className="ov-btn" style={{ padding: "6px 8px" }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.ink3} strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
            </div>
            <PDFViewer width="100%" height="100%" style={{ flex: 1, border: "none" }}>
              <AuditPdfDocument audit={selectedAudit} lang={lang} />
            </PDFViewer>
          </div>
        </div>
      )}
    </div>
  );
}
